// react
import { useMemo } from 'react';

// components
import { ArticleCard } from '@/components/atoms/ArticleCard/ArticleCard';
import { CarouselSlick } from '@/components/atoms/CarouselSlick/CarouselSlick';

// store
import { Article } from '@karehero/models';

// hooks
import { useWindowSizeBreakpoints } from '@/hooks';

// mui
import { Box, Button, Stack } from '@mui/material';

/**
 * SuggestedArticles displays a list of suggested articles.
 */
export const SuggestedArticles = ({
  articles,
  bookmarkedArticles,
  onBookmark,
  onArticleClick,
  isLoading = true,
}: SuggestedArticlesProps) => {
  // hooks
  const { isMd } = useWindowSizeBreakpoints();

  // memos
  const articleItems = useMemo(
    () =>
      (articles || [{}, {}, {}, {}]).map((article, i) => ({
        id: article._id || String(i),
        renderItem: (
          <ArticleCard
            article={article}
            isBookmarked={
              !!bookmarkedArticles?.find((a) => a._id === article._id)
            }
            onBookmark={onBookmark}
            onClick={onArticleClick}
            isLoading={isLoading}
          />
        ),
      })),
    [articles, bookmarkedArticles, onBookmark, isLoading],
  );

  return (
    <Stack spacing={2}>
      {isMd ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
          }}
        >
          {articleItems.map((item) => item.renderItem)}
        </Box>
      ) : (
        <CarouselSlick
          items={articleItems.map((item, i) => ({
            id: item.id,
            renderItem: (
              <Box
                sx={{
                  marginRight: i === articleItems.length - 1 ? 0 : 1,
                }}
              >
                {item.renderItem}
              </Box>
            ),
          }))}
        />
      )}
    </Stack>
  );
};

export interface SuggestedArticlesProps {
  articles?: Partial<Article>[];
  bookmarkedArticles?: Article[];
  onBookmark?: (article: Partial<Article>) => void;
  onArticleClick?: (article?: Partial<Article>) => void;
  isLoading?: boolean;
}

SuggestedArticles.displayName = 'SuggestedArticles';

export default SuggestedArticles;
